import { CommunityWebViewModal } from '@/components/CommunityWebViewModal';
import { useAuth } from '@/lib/AuthContext';
import { communityNextPath, eventsNextPath } from '@/lib/communityLinks';
import { URLS } from '@/lib/constants';
import { readMembershipSource } from '@/lib/membershipSource';
import { isMemberTier } from '@/lib/membershipTier';
import { getCustomerInfo, hasActiveEntitlement } from '@/lib/purchases/RevenueCat';
import { openWpUrlInBrowserLoggedIn } from '@/lib/wpBrowserAutologin';
import { useRouter } from 'expo-router';
import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { Linking, Platform } from 'react-native';

// Dormant alternative for non-subscribers tapping an event link: send them to
// the web /join page in the browser instead of the native paywall. Before
// flipping this on, the bridge autologin gate must accept non-members (it
// currently 403s them), or use a plain (logged-out) /join URL as below.
const SEND_NON_MEMBERS_TO_WEB_JOIN = false;

type CommunityWebViewContextType = {
  /**
   * Open a community URL (The Hang) in the in-app autologin WebView.
   * Non-members are routed to the native paywall instead. No-op on web.
   */
  openCommunity: (url: string) => void;
  /**
   * Open an events URL (/os-sessions, /events) by membership tier:
   * pro → in-app autologin WebView; OS member → default browser, logged in
   * via one-time autologin; non-member → native paywall (event entry points
   * are hidden for them; this covers stray links). No-op on web.
   */
  openEvent: (url: string) => void;
};

const CommunityWebViewContext = createContext<CommunityWebViewContextType | undefined>(undefined);

export function CommunityWebViewProvider({ children }: { children: ReactNode }) {
  const [path, setPath] = useState<string | null>(null);
  const { membershipTier, membershipLoading, preferenceMetadata } = useAuth();
  const router = useRouter();

  const openCommunity = useCallback(
    (url: string) => {
      if (Platform.OS === 'web') return;
      // Known non-member → straight to the paywall, no modal flash. While the
      // tier is still loading we open the modal and let the server-side
      // membership check (the source of truth) decide.
      if (!membershipLoading && !isMemberTier(membershipTier)) {
        router.push('/upgrade');
        return;
      }
      setPath(communityNextPath(url));
    },
    [membershipLoading, membershipTier, router],
  );

  const openEvent = useCallback(
    (url: string) => {
      if (Platform.OS === 'web') return;
      const next = eventsNextPath(url);
      if (!next) {
        // Not actually an events URL — behave like a normal external link.
        Linking.openURL(url).catch(() => {});
        return;
      }
      // Tier still loading: open the WebView and let the server-side
      // membership check decide (mirrors openCommunity).
      if (membershipLoading || membershipTier === 'pro') {
        setPath(next);
        return;
      }
      if (isMemberTier(membershipTier)) {
        void (async () => {
          // Apple-billed OS member (device holds the sub, or flagged apple):
          // WebView, so the event page's /go-pro CTA gets intercepted into
          // the native Apple upgrade instead of web checkout (double charge).
          const appleBilled =
            readMembershipSource(preferenceMetadata) === 'apple' ||
            hasActiveEntitlement(await getCustomerInfo());
          if (appleBilled) {
            setPath(next);
            return;
          }
          // Web-billed OS member: default browser, logged in, where the event
          // page's own gating shows their true state (e.g. upgrade to Pro).
          void openWpUrlInBrowserLoggedIn(url, next);
        })();
        return;
      }
      if (SEND_NON_MEMBERS_TO_WEB_JOIN) {
        Linking.openURL(URLS.JOIN).catch(() => {});
        return;
      }
      router.push('/upgrade');
    },
    [membershipLoading, membershipTier, preferenceMetadata, router],
  );

  return (
    <CommunityWebViewContext.Provider value={{ openCommunity, openEvent }}>
      {children}
      {Platform.OS !== 'web' && (
        <CommunityWebViewModal path={path} onClose={() => setPath(null)} />
      )}
    </CommunityWebViewContext.Provider>
  );
}

/**
 * Tolerant accessor: returns null outside the provider (e.g. auth screens),
 * so callers can fall back to the default external-link behavior.
 */
export function useCommunityWebView(): CommunityWebViewContextType | null {
  return useContext(CommunityWebViewContext) ?? null;
}
